import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import {
   UserCallRecordingExtensionGroupInput,
   UserCallRecordingExtensionInput,
   UserPostInput,
} from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DetailView } from 'src/app/common-components/detail/detail-view';
import { TooltipService } from 'src/app/common-components/tooltip/tooltip-service';
import { userLogOutConfirmationPermissions } from 'src/app/common-components/user-logout-dialog/user-logout-dialog';
import { ExtensionGroupOption } from '../../extension-groups-register/models/extension-group-option.interface';
import { ExtensionOption } from '../../extension-register/models/extension-option.interface';
import { UserReportViewModel } from '../../report-permissions-list/models/user-report-permission.model';
import { PrivilegeView } from '../../role-details/models/role-detail-view.model';
import { UserDetailSaveEvent } from '../models/user-detail-form-submit-event';
import { UserDetailView } from '../models/user-detail-view.model';
import { UserPrivileges } from '../models/user-privileges';
import { UserVerificationEmailRequestModel } from '../models/user-verification-email-request.model';
import { UserDetailFormService } from '../services/user-detail-form.service';
import * as UserDetailActions from '../state/user-detail.actions';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: UserDetailViewComponent }],
   selector: 'ndt-user-detail-view',
   styleUrls: ['./user-detail-view.component.scss'],
   templateUrl: './user-detail-view.component.html',
   standalone: false,
})
export class UserDetailViewComponent extends DetailView implements OnDestroy {
   @Input() callState: CallState;

   @Input() accountId: number | null;

   @Input() isAdminUser: boolean;

   @Input() isSystemUser: boolean;

   @Input() extensions: ExtensionOption[];

   @Input() extensionGroups: ExtensionGroupOption[];

   @Input() get user(): UserDetailView | null {
      return this._user;
   }

   set user(value: UserDetailView | null) {
      if (value) {
         this._user = value;
         this._userLoaded = true;
         this.updateForm();
      }
   }

   @Input() set userPrivileges(privileges: UserPrivileges) {
      this._userPrivileges = privileges;
      if (!privileges?.canEdit) {
         this.form.disable();
      } else if (privileges.canEdit) {
         this.form.enable();
      }
      this.changeDetectorRef.markForCheck();
   }

   get userPrivileges(): UserPrivileges {
      return this._userPrivileges;
   }

   @Input() set privileges(privileges: PrivilegeView[] | null) {
      this._privileges = privileges;
      if (privileges) {
         this._privilegesLoaded = true;
         this.updateForm();
      }
   }
   get privileges(): PrivilegeView[] | null {
      return this._privileges;
   }

   private _userLoaded: boolean = false;
   private _privilegesLoaded: boolean = false;

   @Input() isOnSystemPage: boolean;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<UserDetailSaveEvent>();

   @Output() deleteUser = new EventEmitter<{ userId: number; name: string }>();

   @Output() unlockUser = new EventEmitter<{ userId: number; name: string }>();

   @Output() resendVerificationEmail = new EventEmitter<UserVerificationEmailRequestModel>();

   form: UntypedFormGroup;

   searchControl = new UntypedFormControl('');

   private _user: UserDetailView | null;

   private _userPrivileges: UserPrivileges;

   private _privileges: PrivilegeView[] | null;

   private destroyed$ = new Subject<void>();

   reportsPermissionsChanged: boolean = false;

   selectedIndex: number;

   loadSuccess$ = this.actions$.pipe(
      ofType(UserDetailActions.loadUserDetailsSuccess, UserDetailActions.loadSystemUserDetailsSuccess)
   );

   constructor(
      public formService: UserDetailFormService,
      private confirmationDialogService: ConfirmationDialogService,
      protected changeDetectorRef: ChangeDetectorRef,
      private actions$: Actions,
      private tooltipService: TooltipService
   ) {
      super();
      this.form = this.formService.form;
   }
   ngOnDestroy(): void {
      this.tooltipService.destroyAllTooltips();
      this.destroyed$.next();
   }

   updateForm(): void {
      if (this._userLoaded && this._privilegesLoaded) {
         this.formService.updateUser(this._user as UserDetailView);
      }
   }

   onSubmit(): void {
      const submitEvent = this.getFormSubmittedEvent();
      if (submitEvent) {
         if (submitEvent.userPrivilegeFields.length > 0 || this.reportsPermissionsChanged) {
            userLogOutConfirmationPermissions(this.confirmationDialogService)
               .afterClosed()
               .pipe(takeUntil(this.destroyed$))
               .subscribe((confirmed) => {
                  if (confirmed) {
                     this.formSubmitted.emit(submitEvent);
                  }
               });
         } else {
            this.formSubmitted.emit(submitEvent);
         }
      }
   }

   onCancel(): void {
      this.formSaveCanceled.emit();
   }

   onDeleteClick(): void {
      if (this.user) {
         this.deleteUser.emit({
            name: this.user.name ?? '',
            userId: this.user.userId,
         });
      }
   }

   onResendVerificationEmailClick(): void {
      if (this.user?.username && this.user?.userId && this.user?.emailAddress) {
         this.resendVerificationEmail.emit({
            username: this.user.username,
            userId: this.user.userId,
            emailAddress: this.user.emailAddress,
         });
      }
   }

   onUnlockUserAccount(): void {
      if (this.user) {
         this.unlockUser.emit({ name: this.user.name ?? '', userId: this.user.userId });
      }
   }

   emitRoleChange() {
      this.form.controls.roles.markAsDirty();
   }

   handleReportsChange(reports: UserReportViewModel[]): void {
      this.reportsPermissionsChanged = true;
      this.formService.form.get('userReports')?.setValue(reports);
      this.formService.form.get('userReports')?.markAsDirty();
   }

   private getFormSubmittedEvent(): UserDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.user) {
         return null;
      }

      const user = this.formService.getValue(this.user.userId);

      let userFields: (keyof UserPostInput)[] = [];
      userFields = this.formService.addFieldForDirtyControl(userFields, 'name');
      userFields = this.formService.addFieldForDirtyControl(userFields, 'emailAddress');
      userFields = this.formService.addFieldForDirtyControl(userFields, 'isActive');
      userFields = this.formService.addFieldForDirtyControl(userFields, 'isAdmin');
      userFields = this.formService.addFieldForDirtyControl(userFields, 'isSystemUser');
      userFields = this.formService.addFieldForDirtyControl(userFields, 'account', 'accountId');
      userFields = this.formService.addFieldForDirtyControl(userFields, 'extension', 'extensionId');
      userFields = this.formService.addFieldForDirtyControl(userFields, 'roles', 'roles');

      let userPrivilegeFields: string[] = [];
      if (user.userPrivileges) {
         if (this.privileges) {
            this.privileges.forEach((privilege) => {
               userPrivilegeFields = this.formService.addFieldForDirtyControl(
                  userPrivilegeFields,
                  `userPrivileges.${privilege.privilegeId}.isAdd`,
                  `${privilege.privilegeId}.isAdd`
               );
               userPrivilegeFields = this.formService.addFieldForDirtyControl(
                  userPrivilegeFields,
                  `userPrivileges.${privilege.privilegeId}.isView`,
                  `${privilege.privilegeId}.isView`
               );
               userPrivilegeFields = this.formService.addFieldForDirtyControl(
                  userPrivilegeFields,
                  `userPrivileges.${privilege.privilegeId}.isEdit`,
                  `${privilege.privilegeId}.isEdit`
               );
               userPrivilegeFields = this.formService.addFieldForDirtyControl(
                  userPrivilegeFields,
                  `userPrivileges.${privilege.privilegeId}.isDelete`,
                  `${privilege.privilegeId}.isDelete`
               );
               userPrivilegeFields = this.formService.addFieldForDirtyControl(
                  userPrivilegeFields,
                  `userPrivileges.${privilege.privilegeId}.isEnabled`,
                  `${privilege.privilegeId}.isEnabled`
               );
            });
         }
      }

      let userCallRecordingExtensionFields: (keyof UserCallRecordingExtensionInput)[] = [];

      userCallRecordingExtensionFields = this.formService.addFieldForDirtyControl(
         userCallRecordingExtensionFields,
         'userRecordingExtensions',
         'extensionId'
      );
      userCallRecordingExtensionFields = this.formService.addFieldForDirtyControl(
         userCallRecordingExtensionFields,
         'userRecordingExtensions',
         'isInclude'
      );
      userCallRecordingExtensionFields = this.formService.addFieldForDirtyControl(
         userCallRecordingExtensionFields,
         'userRecordingExtensions',
         'userCallRecordingExtensionId'
      );
      userCallRecordingExtensionFields = this.formService.addFieldForDirtyControl(
         userCallRecordingExtensionFields,
         'userRecordingExtensions',
         'userId'
      );

      userFields = this.formService.addFieldForDirtyControl(
         userFields,
         'userRecordingExtensions',
         'hasAllExtensionRecordings'
      );

      let userCallRecordingExtensionGroupFields: (keyof UserCallRecordingExtensionGroupInput)[] = [];

      userCallRecordingExtensionGroupFields = this.formService.addFieldForDirtyControl(
         userCallRecordingExtensionGroupFields,
         'userRecordingExtensions',
         'extensionGroupId'
      );
      userCallRecordingExtensionGroupFields = this.formService.addFieldForDirtyControl(
         userCallRecordingExtensionGroupFields,
         'userRecordingExtensions',
         'isInclude'
      );
      userCallRecordingExtensionGroupFields = this.formService.addFieldForDirtyControl(
         userCallRecordingExtensionGroupFields,
         'userRecordingExtensions',
         'userCallRecordingExtensionGroupId'
      );
      userCallRecordingExtensionGroupFields = this.formService.addFieldForDirtyControl(
         userCallRecordingExtensionGroupFields,
         'userRecordingExtensions',
         'userId'
      );

      return {
         user,
         userFields,
         userPrivilegeFields,
         userCallRecordingExtensionFields,
         userCallRecordingExtensionGroupFields,
         isSystem: this.isOnSystemPage,
      };
   }
}
